import React from 'react';
import LoadingIndicator from 'generic/LoadingIndicator.react';
import {GoogleContext} from 'client/components/Google';
import {upperFirst} from 'lib/stringUtils';

import './google-classroom-course-detail.scss';

const attributes = ['section', 'room'];

interface GoogleClassroomMeta {
  getId: () => string | number;
  get: (key: string) => string | undefined;
}

/**
 * `GoogleContext` as this component reads it. It is re-exported through the untyped
 * `components/Google` barrel, so `React.ContextType<typeof GoogleContext>` recovers
 * only `any` — hence the structural shape rather than the provider's own type.
 */
interface GoogleContextValue {
  isSignedIn?: boolean;
  isRequestPending?: boolean;
  classrooms: {
    find: (
      predicate: (classroom: GoogleClassroomMeta) => boolean
    ) => GoogleClassroomMeta | undefined;
  };
}

interface Props {
  courseId?: string | number;
}

function generateClassroomMetaLabels(classroom: GoogleClassroomMeta, className: string) {
  return attributes.reduce<React.ReactElement[]>((acc, key) => {
    const val = classroom.get(key);
    if (val) {
      acc.push(
        <span className={className} key={key}>
          {upperFirst(key)}: {val}
        </span>
      );
    }
    return acc;
  }, []);
}

export default class CourseDetail extends React.Component<Props> {
  static contextType = GoogleContext;

  declare context: GoogleContextValue;

  render() {
    if (!this.context.isSignedIn) {
      return null;
    }

    if (this.context.isRequestPending) {
      return <LoadingIndicator inline />;
    }

    const classroom = this.context.classrooms.find(
      (googleClassroom) => googleClassroom.getId() === this.props.courseId
    );

    if (!classroom) {
      return (
        <div className='google-classroom-course-detail'>
          There was an error obtaining your Google Classroom data.
        </div>
      );
    }

    return (
      <div className='google-classroom-course-detail'>
        <b>Google Classroom</b>
        <div className='google-classroom-course-detail__body'>
          <a
            className='google-classroom-course-detail__link'
            href={classroom.get('alternate_link')}
            rel='noopener'
            target='_blank'
          >
            {classroom.get('name')}
          </a>
          {generateClassroomMetaLabels(classroom, 'google-classroom-course-detail__meta')}
          <small>
            This is the classroom that assignments will be sent to when using{' '}
            <em>Post to Google Classroom</em>.
            <br />
            In order to change this link, sync your roster to the desired class.
          </small>
        </div>
      </div>
    );
  }
}
